import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  Divider,
  IconButton
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { addShowcaseReply, updateShowcaseReply, deleteShowcaseReply } from '@services/showcaseService';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import './CommentsDialog.scss';
import type { Showcase, ShowcaseReply } from '@app-types/models/showcase';

interface CommentsDialogProps {
  open: boolean;
  onClose: () => void;
  showcase: Showcase | null;
  onCommentUpdate: () => void;
}

const CommentsDialog: React.FC<CommentsDialogProps> = ({ open, onClose, showcase, onCommentUpdate }) => {
  const { t } = useTranslation();
  const [replyContents, setReplyContents] = useState<Record<string, string>>({});
  const [editingReply, setEditingReply] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  if (!showcase) {
    return null;
  }

  const handleAddReply = async (commentId: string) => {
    const replyContent = replyContents[commentId];
    if (!replyContent?.trim()) return;

    try {
      await addShowcaseReply({
        showcaseId: showcase._id,
        content: replyContent.trim(),
        commentId
      });
      setReplyContents(prev => ({ ...prev, [commentId]: '' }));
      onCommentUpdate();
      toast.success(t('showcaseManagement.replyAdded'));
    } catch (error) {
      console.error('Error adding reply:', error);
      toast.error(t('showcaseManagement.failedToAddReply'));
    }
  };

  const handleUpdateReply = async (commentId: string, replyId: string) => {
    if (!editContent.trim()) return;

    try {
      await updateShowcaseReply({
        showcaseId: showcase._id,
        commentId,
        replyId,
        content: editContent.trim()
      });
      setEditingReply(null);
      setEditContent('');
      onCommentUpdate();
      toast.success(t('showcaseManagement.replyUpdated'));
    } catch (error) {
      console.error('Error updating reply:', error);
      toast.error(t('showcaseManagement.failedToUpdateReply'));
    }
  };

  const handleDeleteReply = async (commentId: string, replyId: string) => {
    try {
      await deleteShowcaseReply({
        showcaseId: showcase._id,
        commentId,
        replyId
      });
      onCommentUpdate();
      toast.success(t('showcaseManagement.replyDeleted'));
    } catch (error) {
      console.error('Error deleting reply:', error);
      toast.error(t('showcaseManagement.failedToDeleteReply'));
    }
  };

  const handleReplyChange = (commentId: string, value: string) => {
    setReplyContents(prev => ({ ...prev, [commentId]: value }));
  };

  const startEditing = (reply: ShowcaseReply) => {
    setEditingReply(reply._id);
    setEditContent(reply.content);
  };

  const cancelEditing = () => {
    setEditingReply(null);
    setEditContent('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="comments-dialog">
      <DialogTitle>{t('showcaseManagement.comments')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {showcase.comments?.map((comment) => (
            <Box key={comment._id} className="comment">
              <Box className="comment__content">
                <Typography variant="body1">{comment.content}</Typography>
                <Typography variant="caption" className="comment__timestamp">
                  {new Date(comment.createdAt ?? '').toLocaleString()}
                </Typography>
              </Box>
              <Box className="comment__replies">
                {comment.replies?.map((reply) => (
                  <Box key={reply._id} className="comment__reply">
                    {editingReply === reply._id ? (
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          value={editContent}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditContent(e.target.value)}
                          size="small"
                          className="comment__reply-input"
                        />
                        <Box className="comment__reply-buttons">
                          <Button size="small" onClick={cancelEditing}>
                            {t('commonConfirmationDialog.cancel')}
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleUpdateReply(comment._id, reply._id)}
                            disabled={!editContent.trim()}
                          >
                            {t('showcaseManagement.update')}
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box className="comment__reply-content">
                        <Box className="comment__reply-text">
                          <Typography variant="body2">{reply.content}</Typography>
                          <Typography variant="caption" className="comment__timestamp">
                            {new Date(reply.createdAt ?? '').toLocaleString()}
                          </Typography>
                        </Box>
                        <Box className="comment__reply-actions">
                          <IconButton
                            size="small"
                            onClick={() => startEditing(reply)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteReply(comment._id, reply._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </Box>
                ))}
                <Box className="comment__reply">
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    value={replyContents[comment._id] || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleReplyChange(comment._id, e.target.value)}
                    placeholder={t('showcaseManagement.addReply')}
                    size="small"
                    className="comment__reply-input"
                  />
                  <Box className="comment__reply-buttons">
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleAddReply(comment._id)}
                      disabled={!replyContents[comment._id]?.trim()}
                    >
                      {t('showcaseManagement.reply')}
                    </Button>
                  </Box>
                </Box>
              </Box>
              <Divider className="comment__divider" />
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Box sx={{ width: '100%', p: 2 }}>
          <Button onClick={onClose}>
            {t('common.close')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CommentsDialog;